// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

const NF = new Intl.NumberFormat('en-AU');

export function num(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return NF.format(Math.round(n));
}

/**
 * Area, at the precision the measurement actually supports.
 *
 * These figures come from dissolving indicative boundaries, so printing
 * 3,254,573 km² to the last square kilometre implies a precision the source
 * geometry does not have. Above ten thousand it rounds to three significant
 * figures; the full value stays available in tooltips and the methodology for
 * anyone reconciling against the register.
 */
export function km2(n: number | null | undefined, opts: { exact?: boolean } = {}): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (opts.exact) return `${NF.format(Math.round(n))} km²`;
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(2)} million km²`;
  if (abs >= 10_000) return `${NF.format(Math.round(n / 1000) * 1000)} km²`;
  if (abs >= 1) return `${NF.format(Math.round(n))} km²`;
  return `${n.toFixed(2)} km²`;
}

export function pct(n: number | null | undefined, dp = 1): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(dp)}%`;
}

/** A date the way a register states it: 4 September 1998. */
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export function longDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

export function shortDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function yearOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const y = Number(iso.slice(0, 4));
  return Number.isFinite(y) ? y : null;
}

/** A duration a reader can hold in their head. */
export function duration(days: number | null | undefined): string {
  if (days == null || !Number.isFinite(days)) return '—';
  if (days < 0) return '—';
  if (days < 60) return `${days} day${days === 1 ? '' : 's'}`;
  const years = days / 365.25;
  if (years < 2) return `${Math.round(days / 30.44)} months`;
  return `${years.toFixed(1)} years`;
}

export function esc(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Text for the `data-tip` ATTRIBUTE, set via setAttribute.
 *
 * setAttribute takes RAW newlines — the CSS `white-space: pre-line` on the
 * tooltip renders them. Passing `&#10;` here would print the literal characters
 * "&#10;" to the reader; that encoding is only correct when building an HTML
 * string, which is what `tipAttr` is for.
 */
export function tip(...lines: (string | null | undefined | false)[]): string {
  return lines.filter(Boolean).join('\n');
}

/** The same tooltip text, for embedding inside an innerHTML string. */
export function tipAttr(...lines: (string | null | undefined | false)[]): string {
  return esc(lines.filter(Boolean).join('\n')).replace(/\n/g, '&#10;');
}

export function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

/**
 * Plural without the "(s)" that makes prose read like a form.
 * `plural(1,'determination')` → "1 determination".
 */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${NF.format(n)} ${n === 1 ? one : many}`;
}
